import React, { useRef, useState } from "react";
import { View } from "react-native";

import { DialogAction } from "./DialogAction";
import { DialogCheckbox } from "./DialogCheckbox";
import { DialogContent } from "./DialogContent";
import { DialogDescription } from "./DialogDescription";
import { DialogTitle } from "./DialogTitle";

import type { FC } from "react";
import type { CheckedType } from "../CheckboxBase";
import type { DialogBaseProps } from "./DialogBase";

export interface AlertDialogBaseProps
  extends Pick<
    DialogBaseProps,
    "visible" | "onClose" | "testID" | "backdropOpacity"
  > {
  title: string;
  description?: string | React.ReactElement;
  titleLabel?: string;
  descriptionLabel?: string;
  alertText?: string;
  Dialog: FC<DialogBaseProps>;
  AutoFocusInside?: FC<any> | React.ComponentType<any>;
  onChangeChecked?: (checked: CheckedType) => void;
  checkboxLabel?: string;
}

// on web we want the confirm button to be autofocused for accessibility
// we don't want anything to happen on native
const PassThrough: FC<{ children: any }> = ({ children }) => {
  return <>{children}</>;
};

export function AlertDialogBase({
  title,
  backdropOpacity,
  description,
  titleLabel,
  descriptionLabel,
  visible,
  alertText = "Ok",
  onClose,
  testID,
  Dialog,
  AutoFocusInside = PassThrough,
  onChangeChecked,
  checkboxLabel,
}: AlertDialogBaseProps) {
  const buttonRef = useRef<View>(null);
  const [checked, setChecked] = useState(false);

  return (
    <Dialog
      testID={testID}
      titleID={"title"}
      descriptionID={"desc"}
      visible={visible}
      backdropOpacity={backdropOpacity}
      onClose={() => {
        onClose();
        setChecked(false);
      }}
    >
      <DialogContent>
        <View
          style={{
            paddingVertical: 16,
            paddingHorizontal: 24,
            alignItems: "center",
            maxWidth: 328,
          }}
        >
          <AutoFocusInside>
            <DialogTitle
              style={{ textAlign: "center" }}
              nativeID="title"
              accessibilityLabel={titleLabel}
            >
              {title}
            </DialogTitle>
          </AutoFocusInside>

          {description ? (
            <DialogDescription
              nativeID="desc"
              accessibilityLabel={descriptionLabel}
            >
              {description}
            </DialogDescription>
          ) : null}
          {/* this does nothing on native but on web it will autofocus the primary input per aria accessibility guidelines */}
          <DialogAction
            type="primary"
            ref={buttonRef}
            onPress={() => {
              onClose();
              checked && onChangeChecked ? onChangeChecked(checked) : null;
            }}
            text={alertText}
            style={{
              width: 280,
            }}
          />
          {onChangeChecked ? (
            <DialogCheckbox
              checked={checked}
              onChangeChecked={setChecked}
              label={checkboxLabel}
              style={{ marginTop: 16 }}
            />
          ) : null}
        </View>
      </DialogContent>
    </Dialog>
  );
}
